// Bella Vita frontend — History API router (SPA).
//
//  - Real routes, real back/forward, deep-linkable.
//  - Preserves the design-v2 page inventory (owner rule §11).
//  - Re-renders on language change and re-applies <html lang/dir> every nav.
//  - Route-change focus management for accessibility (rule §17).
//  - Unknown paths -> 404 view (nginx serves index.html for any path; see
//    nginx/bellavita-frontend.conf.example).

import { $, mount } from './lib/dom.js';
import { applyDocumentLang, onLangChange } from './locale/index.js';

/** @type {{ pattern:RegExp, keys:string[], load:() => Promise<{default:Function}>, raw:string }[]} */
let table = [];
let mountEl = null;
let notFoundLoader = null;
let current = null; // { path, params }

function compile(path) {
  const keys = [];
  const pattern = new RegExp(
    '^' +
      path.replace(/:[^/]+/g, (m) => {
        keys.push(m.slice(1));
        return '([^/]+)';
      }).replace(/\//g, '\\/') +
      '\\/?$',
  );
  return { pattern, keys };
}

export function defineRoutes(defs) {
  table = defs.map((d) => ({ ...compile(d.path), load: d.load, raw: d.path }));
}
export function setNotFound(loader) {
  notFoundLoader = loader;
}

function match(pathname) {
  for (const r of table) {
    const m = r.pattern.exec(pathname);
    if (m) {
      const params = {};
      r.keys.forEach((k, i) => (params[k] = decodeURIComponent(m[i + 1])));
      return { route: r, params };
    }
  }
  return null;
}

async function render() {
  applyDocumentLang();
  const { pathname } = window.location;
  const hit = match(pathname);
  const loader = hit ? hit.route.load : notFoundLoader;
  const params = hit ? hit.params : {};
  current = { path: pathname, params };

  let view;
  try {
    const mod = await loader();
    view = await (mod.default || mod)({ params, navigate });
  } catch (err) {
    console.error('[router] view failed:', err);
    const mod = await notFoundLoader();
    view = await (mod.default || mod)({ params: {}, navigate });
  }

  mount(mountEl, view);
  // Accessibility: move focus to the fresh view's <main> (shells render one),
  // falling back to the mount host. Reset scroll on every navigation.
  const focusTarget = mountEl.querySelector('#bv-main') || mountEl;
  if (!focusTarget.hasAttribute('tabindex')) focusTarget.setAttribute('tabindex', '-1');
  focusTarget.focus({ preventScroll: true });
  window.scrollTo(0, 0);
}

export function navigate(to, { replace = false } = {}) {
  const url = new URL(to, window.location.origin);
  if (url.pathname === window.location.pathname && url.search === window.location.search) return;
  if (replace) history.replaceState({}, '', url);
  else history.pushState({}, '', url);
  render();
}

export function getCurrent() {
  return current;
}

export function startRouter({ mount: sel = '#bv-app' } = {}) {
  mountEl = $(sel);
  window.addEventListener('popstate', render);
  // Intercept in-app links marked data-link (progressive enhancement).
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[data-link]');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || href.startsWith('http') || a.target === '_blank') return;
    e.preventDefault();
    navigate(href);
  });
  onLangChange(() => render());
  render();
}
